import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { DE } from "../../Languages/DE";
import { EN } from "../../Languages/EN";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { Scrollbars } from "react-custom-scrollbars";
import _ from "lodash";
import { BASE_URL } from "../../helper/constants";
import { $crud } from "../../helper/CrudFactory";
import { BsFillXCircleFill } from "react-icons/bs";
import { $user } from "../../helper/UserFactory";
import { convertCapitalLeter, convertPosition } from "../../helper/convertor";
import { store, UPDATE_PROJECT_ACTION_FIRE } from "../../store";
import Switch from "react-switch";
import { useSelector } from "react-redux";
import ModernToggleSwitch from "../../components/ModernToggleSwitch";
import {
  ContactSourceTabs,
  TeamsContactsPanel,
  attachTeamsCopy,
  notifyTeamsTooMany,
  startTeamsOnlyChat,
  teamsPickerText,
  useTeamsAccounts,
  useTeamsPeopleSearch,
} from "../../components/chat/TeamsContacts";
import { classifyChatSelection, mergeTeamsPeople, teamsPersonName, teamsPickerState, toggleTeamsPerson } from "../../utils/teams/combinedChat";
import { ChatListAvatar } from "../Feeds/ChatListParts";
import "./AddProjectChatUser.css";

const renderThumb = ({ style, ...props }) => {
  const thumbStyle = {
    borderRadius: 6,
    backgroundColor: "rgba(var(--kjuup-accent-rgb), 0.8)",
  };
  return (
    <div
      className="thumb-style"
      style={{ ...style, ...thumbStyle }}
      {...props}
    />
  );
};

const CustomScrollbars = (props) => (
  <Scrollbars
    renderThumbHorizontal={renderThumb}
    renderThumbVertical={renderThumb}
    {...props}
  />
);

// New chat in a project (no groupId / addAsMember): with a writable Microsoft Teams account
// (`teamsEnabled`) the picker has "Kjuup contacts | Teams contacts" under the one search field.
//   Kjuup only  a Kjuup group in the project (as before)
//   Teams only  a Teams chat filed under the project (POST /teams/chats with buildingId)
//               → props.onTeamsChatCreated({ groupId, chatType, name, project })
//   both        the Kjuup group (POST /group with building), then its Teams copy (POST /teams/bridges)
// `teamsOnly` (Kjuup chats hidden): only the Teams contacts.
// Helpers instead of `&&` / `?:` in the component body before its hooks (eslint-plugin-react-hooks
// path counting). A new chat (not "add members" to a group) may pick Teams contacts.
const isNewChatPicker = (props) => !props.addAsMember && !props.groupId;
const teamsContactsWanted = (props) => !!props.teamsEnabled && isNewChatPicker(props);
// The Teams list instead of the Kjuup one: on its tab, before the name step. Teams only (Kjuup
// chats hidden): from the start, "Loading…" until the accounts are in.
const teamsPanelShown = ({ props, creatingChat, teamsTabOn, contactTab, continueAction }) =>
  !!props.teamsEnabled && creatingChat && (teamsTabOn || !!props.teamsOnly) && contactTab === "teams" && !continueAction;

function AddProjectChatUser(props) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const [search, setSearch] = useState("");
  const [existingUsers, setExistingUsers] = useState([]);
  const [continueAction, setContinueAction] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [checkToggle, setCheckToggle] = useState(false)
  const [isAllMember, setIsAllMember] = useState(false)
  const [loader, setLoader] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [creationStep, setCreationStep] = useState(null)
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [openCompanies, setOpenCompanies] = useState(new Set());
  const selectedMapRef = React.useRef({});
  const existingUsersRef = React.useRef([]);
  const companyGroupsInitialized = React.useRef(false);
  const user = useSelector((state) => state.user);

  // Microsoft Teams contacts (new chats only): the visible tab and the picked Teams people.
  const creatingChat = isNewChatPicker(props);
  const [contactTab, setContactTab] = useState(() => (props.teamsOnly ? "teams" : "kjuup"));
  const [selectedTeams, setSelectedTeams] = useState([]);
  const teamsAccountsState = useTeamsAccounts(teamsContactsWanted(props));
  const teamsAccounts = teamsAccountsState.accounts;
  const teamsAccountId = teamsAccountsState.accountId;
  const teamsPicker = teamsPickerState({ enabled: teamsContactsWanted(props), accounts: teamsAccounts, selected: selectedTeams });
  const teamsTabOn = teamsPicker.tabOn;
  const pickedTeams = teamsPicker.picked;
  const teamsSearch = useTeamsPeopleSearch({ enabled: teamsTabOn, query: search, accountId: teamsAccountId });
  const selectionKind = classifyChatSelection(
    Object.values(selectedMapRef.current).filter((u) => u.addStatus).map((u) => u.id),
    pickedTeams
  );
  // A Teams-only chat may go without a name (a 1:1 has none); Kjuup groups need one.
  const needsGroupName = selectionKind !== "teams";
  const showTeamsPanel = teamsPanelShown({ props, creatingChat, teamsTabOn, contactTab, continueAction });
  const teamsText = (key, de, en) => teamsPickerText(strings, isGerm, key, de, en);

  const toggleTeamsSelection = (person) => {
    const r = toggleTeamsPerson(selectedTeams, person);
    if (r.error === "too_many") {
      notifyTeamsTooMany(strings, isGerm);
      return;
    }
    setSelectedTeams(r.list);
  };

  // People belong to one Microsoft organisation: another account starts the Teams pick over.
  const pickTeamsAccount = (id) => {
    if (loader || id === teamsAccountId) return;
    teamsAccountsState.setAccountId(id);
    setSelectedTeams([]);
  };

  // Teams contacts only: the Teams chat, filed under this project.
  const startRoomTeamsChat = async () => {
    if (loader || !pickedTeams.length) return;
    setLoader(true);
    const created = await startTeamsOnlyChat({
      people: pickedTeams,
      topic: groupName,
      buildingId: props.project_id,
      accountId: teamsAccountId,
      strings,
      isGerm,
    });
    setLoader(false);
    if (!created) return;
    if (props.handleEditClose) props.handleEditClose();
    if (props.onTeamsChatCreated) props.onTeamsChatCreated(created);
  };

  // Kjuup + Teams: the new group's Teams copy. A failure keeps the group (toast only).
  const attachTeamsCopyIfCombined = async (chat) => {
    if (selectionKind !== "combined" || !chat || !chat.id) return;
    await attachTeamsCopy({ groupId: chat.id, people: pickedTeams, accountId: teamsAccountId, strings, isGerm });
  };


  const confirmWithColor = async (opts) => {
    if (props.isInternal) document.body.dataset.confirmColor = "green";
    try { return await $crud.confirm(opts); }
    finally { delete document.body.dataset.confirmColor; }
  };

  const groupByAlpha = (userList) => {
    const uniqueUsers = _.uniqBy(userList || [], "id");
    const arr = _.uniqBy(
      uniqueUsers.map((u) => ({ name: (u.lastName || '').trim()[0]?.toUpperCase() || '#' })),
      "name"
    );
    const sorted = _.sortBy(arr, ["name"]);
    return sorted.map((a) => ({
      ...a,
      names: uniqueUsers.filter((u) => ((u.lastName || '').trim()[0]?.toUpperCase() || '#') === a.name),
    }));
  };

  const dedupeCompanyGroups = (groups) => {
    const seen = new Set();
    return (groups || [])
      .map((cg) => ({
        ...cg,
        users: (cg.users || [])
          .filter((u) => {
            if (!u?.id || seen.has(u.id)) return false;
            seen.add(u.id);
            return true;
          })
          .map((u) => ({
            ...u,
            companyName: u.companyName || cg.companyName || null,
            fullName: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim(),
          })),
      }))
      .filter((cg) => cg.users.length > 0);
  };

  const loadUsers = async (searchStr = "", pageNum = 1, append = false) => {
    if (pageNum === 1 && !append) setLoader(true);
    else setLoadingMore(true);
    try {
      const params = { page: pageNum, limit: 30 };
      if (searchStr) params.query = searchStr;

      let apiPath;
      if (props.groupId) {
        apiPath = `/group/${props.groupId}/invitable-users`;
      } else {
        apiPath = `/group/invitable-users`;
        params.buildingListId = props.project_id;
      }

      const { data } = await callAPI(apiPath, params, 'GET');
      if (!data.status) {
        setLoader(false);
        setLoadingMore(false);
        return;
      }

      const apiUsers = (data.users || []).map((u) => ({
        ...u,
        addStatus: !!selectedMapRef.current[u.id]?.addStatus,
        isChecked: !!selectedMapRef.current[u.id]?.isChecked,
      }));

      let allUsers;
      if (append) {
        const existingFlat = existingUsersRef.current.flatMap((g) => g.names);
        const existingIds = new Set(existingFlat.map((u) => u.id));
        const newUsers = apiUsers.filter((u) => !existingIds.has(u.id));
        allUsers = [...existingFlat, ...newUsers];
      } else {
        allUsers = apiUsers;
      }

      const grouped = groupByAlpha(allUsers);
      setExistingUsers(grouped);
      existingUsersRef.current = grouped;
      setPage(data.pagination?.page || pageNum);
      setTotalPages(data.pagination?.totalPages || 0);

      if (data.companyUsers && !append) {
        const enriched = dedupeCompanyGroups(data.companyUsers).map((cg) => ({
          ...cg,
          users: cg.users.map((u) => ({
            ...u,
            addStatus: !!selectedMapRef.current[u.id]?.addStatus,
            isChecked: !!selectedMapRef.current[u.id]?.isChecked,
          })),
        }));
        setCompanyGroups(enriched);
        if (!companyGroupsInitialized.current) {
          companyGroupsInitialized.current = true;
          if (enriched.length === 1) {
            setOpenCompanies(new Set([enriched[0].companyId]));
          }
        }
      }
    } catch (err) {
      console.error('loadUsers error:', err);
    }
    setLoader(false);
    setLoadingMore(false);
  };

  const debouncedSearch = React.useCallback(
    _.debounce((val) => {
      loadUsers(val, 1, false);
    }, 300),
    [props.groupId, props.project_id]
  );

  const handleScrollFrame = (values) => {
    if (showTeamsPanel) return;
    if (values.top > 0.9 && !loadingMore && !loader && page < totalPages) {
      loadUsers(search, page + 1, true);
    }
  };

  useEffect(() => {
    loadUsers("", 1, false);
    return () => debouncedSearch.cancel();
  }, []);



  // Show privacy modal before creating group
  const handleCreateGroup = () => {
    if (selectionKind === "teams") {
      startRoomTeamsChat();
      return false;
    }
    if (!groupName) {
      $crud.notify({
        type: "error",
        message: "Please enter group name",
      });
      return false;
    }
    // Show privacy confirmation modal
    setShowPrivacyModal(true);
  };

  // Handle privacy choice and proceed with creation
  const handlePrivacyChoice = async (isPrivate) => {
    setShowPrivacyModal(false);
    await addProjectUser(isPrivate);
  };

  const addProjectUser = async (isPrivate = false) => {
    if (!groupName) {
      $crud.notify({
        type: "error",
        message: "Please enter group name",
      });
      return false;
    }
    let selectedMembers = Object.values(selectedMapRef.current).filter((u) => u.addStatus);
    let postUser = [...selectedMembers]
    const id = props.project_id;
    let userIds = postUser.map((u) => {
      return u.id;
    });
    let memberToAdd = []
    postUser.map((u) => {
      if (u.nonmember)
        memberToAdd.push({ user: u.id })
    })
    setLoader(true)
    let closed = false;
    const finishCreatedGroup = (createdChat) => {
      closed = true;
      setCreationStep(null);
      setLoader(false);
      if (props.setAllUsers) {
        props.setAllUsers([]);
      }
      // Close the create form immediately so the user is not left on
      // "add group name" while the chat list refreshes.
      if (props.handleEditClose) {
        props.handleEditClose();
      }
      if (props.refreshChat) {
        Promise.resolve(props.refreshChat(createdChat?.id, createdChat)).catch(() => {});
      }
    };
    try {
      let nonmemberId = postUser?.filter((itm) => itm.nonmember === true);
      if (nonmemberId?.length > 0) {
        setCreationStep('adding_members');
        const { data } = await callAPI(props.isInternal && props.cID ? `/building/${id}/add?companyId=${props.cID}` : `/building/${id}/add`, {}, "PUT", {
          id: id,
          users: memberToAdd,
        });
        if (data.status) {
          setCreationStep('creating_chat');
          const { data } = await callAPI(`/group`, {}, "POST", {
            building: id,
            groupType: "building",
            isOneToOne: false,
            name: groupName,
            users: userIds,
            isPrivet: isPrivate,
          });
          console.log("data", data)

          if (!data.status) {
            $crud.notify({ type: "error", message: data.message });
            return;
          }

          await attachTeamsCopyIfCombined(data.chat);
          setCreationStep('finishing');
          finishCreatedGroup(data.chat);
          return;
        }
        $crud.notify({
          type: "error",
          message: data.message,
        });
        return;
      }

      setCreationStep('creating_chat');
      const { data } = await callAPI(`/group`, {}, "POST", {
        building: id,
        groupType: "building",
        isOneToOne: false,
        name: groupName,
        users: userIds,
        isPrivet: isPrivate,
      });
      console.log("data", data)

      if (!data.status) {
        $crud.notify({ type: "error", message: data.message });
        return;
      }

      await attachTeamsCopyIfCombined(data.chat);
      setCreationStep('finishing');
      finishCreatedGroup(data.chat);
    } catch (err) {
      $crud.notify({
        type: "error",
        message: err?.message || (isGerm ? "Gruppe konnte nicht erstellt werden" : "Could not create group"),
      });
    } finally {
      if (!closed) {
        setCreationStep(null);
        setLoader(false);
      }
    }
  };

  const continueHandler = async () => {
    let selectedMembers = Object.values(selectedMapRef.current).filter((u) => u.addStatus);
    let postUser = [...selectedMembers]
    if (props.addAsMember) {
      await confirmWithColor({
        title: strings.confirm,
        textContent: strings.check_selected_settings,
        options: {
          ok: strings.confirm,
          cancel: strings.cancel
        }
      });
      setLoader(true)
      const id = props.project.id;
      let memberToAdd = []
      postUser.map((u) => {
        if (u.nonmember)
          memberToAdd.push({ user: u.id })
      })
      let ids = []
      for (let obj of postUser) {
        ids.push({ chat_from_start: obj.isChecked ? true : false, user: obj.id })
      }
      ids = ids.filter(e => e.user != user.id)
      if (memberToAdd.length > 0) {
        // let { data } = await callAPI(props.isInternal ? `/building/${id}/add?companyId=${props.cID}` : props.cID ? `/building/${id}/add?companyId=${props.cID}` : `/building/${id}/add`, {}, "PUT", {
        //   users: memberToAdd,
        // });
        let { data } = await callAPI(props.isInternal && props.cID ? `/building/${id}/add?companyId=${props.cID}` : `/building/${id}/add`, {}, "PUT", {
          users: memberToAdd,
        });
        if (data.status) {
          let { data } = await callAPI(`/group/${props.groupId}/add`, {}, "PUT", {
            users: ids,
            building: id
          })
          selectedMapRef.current = {};
          if (props.from != 'group')
            store.dispatch({
              type: UPDATE_PROJECT_ACTION_FIRE,
              updateProjectActionFire: true,
            });
          props.handleEditClose()
        }
        else {
          $crud.notify({
            type: data.status ? "success" : "error",
            message: data.message,
          });
        }
      }
      else {
        let { data } = await callAPI(`/group/${props.groupId}/add`, {}, "PUT", {
          users: ids,
          building: id
        })
        selectedMapRef.current = {};
        if (props.from != 'group')

          store.dispatch({
            type: UPDATE_PROJECT_ACTION_FIRE,
            updateProjectActionFire: true,
          });
        props.handleEditClose()
      }
      setLoader(false)
    }
    else {
      // Teams contacts only (Kjuup chats hidden): nothing to create without a Teams person.
      if (props.teamsOnly && !pickedTeams.length) return;
      const selected = Object.values(selectedMapRef.current).filter((u) => u.addStatus);
      const grouped = groupByAlpha(selected);
      setExistingUsers(grouped);
      existingUsersRef.current = grouped;
      setContinueAction(true);
    }
  };

  useEffect(() => {
    if (checkToggle) {
      let { index, id } = checkToggle
      let tmp = [...existingUsers]
      tmp = tmp.map((e, i) => {
        e.names = e.names.map((j, k) => {
          if (id == j.id) {
            const updated = { ...j, isChecked: !j.isChecked };
            if (selectedMapRef.current[id]) {
              selectedMapRef.current[id] = { ...selectedMapRef.current[id], isChecked: updated.isChecked };
            }
            return updated;
          }
          else {
            return { ...j }
          }
        })
        return { ...e }
      })
      setExistingUsers(tmp)
      existingUsersRef.current = tmp;
      const toggled = tmp.flatMap((e) => e.names).find((j) => j.id === id);
      if (toggled) {
        setCompanyGroups((prev) => prev.map((g) => ({
          ...g,
          users: g.users.map((cu) => cu.id === id ? { ...cu, isChecked: toggled.isChecked } : cu),
        })));
      }
      setCheckToggle(false)
    }
  }, [checkToggle])


  return (
    <React.Fragment>
      <div className="add-project-chat-user">
        <div className="company_header add-project-chat-user__header">
          <div className="heading_icon m-0 add-project-chat-user__header-row">
            <div
              onClick={(e) => {
                if (!continueAction)
                  props.handleEditClose()
                else {
                  setContinueAction(false)
                  loadUsers(search, 1, false);
                }
              }}
              className="pointer add-project-chat-user__cancel"
            >
              <span className={`material-symbols-outlined material-arrow-left ${props.isInternal ? 'internal-arrow' : ''} add-project-chat-user__back-icon`}>
                keyboard_arrow_left
              </span>
              <span className="add-project-chat-user__cancel-label">{strings.cancel}</span>
            </div>
            <div className="add-project-chat-user__title">
              {strings.add_user}
            </div>
            <div
              className={`add-project-chat-user__continue ${(() => {
                if (loader) return "add-project-chat-user__continue--loading";
                const hasSelectedUsers = Object.values(selectedMapRef.current).some((u) => u.addStatus);
                if (props.from === 'group' && !hasSelectedUsers) return "add-project-chat-user__continue--disabled";
                if (props.from !== 'group' && continueAction && needsGroupName && !groupName?.trim()) return "add-project-chat-user__continue--disabled";
                return props.isInternal ? "add-project-chat-user__continue--internal" : "";
              })()}`}
              onClick={() => {
                if (!loader) {
                  const hasSelectedUsers = Object.values(selectedMapRef.current).some((u) => u.addStatus);
                  if (props.from === 'group' && !hasSelectedUsers) return;
                  if (props.from !== 'group' && continueAction && needsGroupName && !groupName?.trim()) return;
                  
                  if (continueAction)
                    handleCreateGroup()
                  else
                    continueHandler()
                }
              }}
            >
              <span className="add-project-chat-user__continue-label">
                {continueAction && selectionKind === "teams"
                  ? teamsText("teams_new_chat_start", "Chat starten", "Start chat")
                  : isGerm ? !continueAction ? DE.continue : DE.create_group : !continueAction ? EN.continue : EN.create_group}
              </span>
              {/* Removed green/orange turning wheel spinner - keep smooth animation with arrow icon */}
              <img
                className="arrow-icon-inline add-project-chat-user__continue-arrow"
                width={16}
                height={16}
                src={
                  (() => {
                    const hasSelectedUsers = existingUsers?.some(user => 
                      user.names?.some(name => name.addStatus)
                    );
                    
                    // Show gray arrow when from='group' with no selection
                    if (props.from === 'group' && !hasSelectedUsers) {
                      return process.env.PUBLIC_URL + "/assets/img/arrow-right-modern-gray.svg";
                    }
                    
                    // Show gray arrow when creating new group without group name
                    if (props.from !== 'group' && continueAction && needsGroupName && !groupName?.trim()) {
                      return process.env.PUBLIC_URL + "/assets/img/arrow-right-modern-gray.svg";
                    }
                    
                    // Otherwise show orange/green arrow (enabled)
                    return process.env.PUBLIC_URL + (props.isInternal ? "/assets/img/arrow-right-modern-green.svg" : "/assets/img/arrow-right-modern-orange.svg");
                  })()
                }
                alt="arrow"
              />
            </div>
          </div>
        </div>
        <div className="add-project-chat-user__body">
          {!continueAction && (
            <div className="search_div p-2 flex-shrink-0">
              <div className="search">
                <Form.Group controlId="searchUserInput">
                  <Form.Control
                    autoComplete="off"
                    value={search}
                    type="text"
                    onChange={(e) => {
                      const val = e.target.value;
                      setSearch(val);
                      debouncedSearch(val);
                    }}
                    placeholder={strings.search || 'Search...'}
                  />
                </Form.Group>
              </div>
              {teamsTabOn && !props.teamsOnly ? (
                <ContactSourceTabs
                  tab={contactTab}
                  onTab={setContactTab}
                  kjuupCount={Object.values(selectedMapRef.current).filter((u) => u.addStatus).length}
                  teamsCount={pickedTeams.length}
                  strings={strings}
                  isGerm={isGerm}
                  className="add-project-chat-user__tabs"
                />
              ) : null}
            </div>
          )}
          <div className="add-project-chat-user__scroll-wrap">
          <CustomScrollbars
            autoHide
            autoHideTimeout={500}
            autoHideDuration={200}
            onScrollFrame={handleScrollFrame}
            renderThumbHorizontal={() => <div style={{ display: 'none', height: 0 }} />}
            renderTrackHorizontal={() => <div style={{ display: 'none', height: 0 }} />}
            renderView={(props) => (
              <div {...props} style={{ ...props.style, overflowX: 'hidden' }} />
            )}
          >
            <div className="invite p-2">
              {showTeamsPanel ? (
                <TeamsContactsPanel
                  accounts={teamsAccounts}
                  accountId={teamsAccountId}
                  onPickAccount={pickTeamsAccount}
                  people={teamsSearch.people === null ? null : mergeTeamsPeople(teamsSearch.people, pickedTeams)}
                  directory={teamsSearch.directory}
                  query={search}
                  selected={pickedTeams}
                  onToggle={toggleTeamsSelection}
                  disabled={loader}
                  strings={strings}
                  isGerm={isGerm}
                />
              ) : (
              <div className="admin_right">
                {continueAction && !(selectionKind === "teams" && pickedTeams.length < 2) && (
                  <div className="search_div">
                    <div className="search">
                      <Form.Group controlId="exampleForm.ControlInput1">
                        <Form.Control
                          autocomplete="off"
                          value={groupName}
                          type="text"
                          maxLength={30}
                          onChange={(e) => setGroupName(e.target.value)}
                          placeholder={selectionKind === "teams"
                            ? teamsText("teams_new_chat_topic", "Gruppenname (optional)", "Group name (optional)")
                            : strings.group_name}
                        />
                        <Form.Text className="text-muted">{30 - groupName.length} {strings.characters_remaining}</Form.Text>
                      </Form.Group>
                    </div>
                  </div>
                )}
                {(props.addAsMember || props.groupId) && !continueAction && Object.values(selectedMapRef.current).filter((u) => u.addStatus).length >= 2 && (
                  <div
                    className="mx-1 mb-2 add-project-chat-user__toggle-all"
                    onClick={async () => {
                      const selectedIds = Object.keys(selectedMapRef.current).filter(
                        (id) => selectedMapRef.current[id]?.addStatus
                      );
                      if (selectedIds.length === 0) return;
                      const anyChecked = selectedIds.some((id) => selectedMapRef.current[id]?.isChecked);
                      const newValue = !anyChecked;
                      await confirmWithColor({
                        title: strings.confirm,
                        textContent: isGerm
                          ? (newValue ? DE.toggle_chat_history_on : DE.toggle_chat_history_off)
                          : (newValue ? EN.toggle_chat_history_on : EN.toggle_chat_history_off),
                        options: { ok: strings.confirm, cancel: strings.cancel }
                      });
                      selectedIds.forEach((id) => {
                        selectedMapRef.current[id] = { ...selectedMapRef.current[id], isChecked: newValue };
                      });
                      let tmp = [...existingUsers];
                      tmp = tmp.map((e) => ({
                        ...e,
                        names: e.names.map((j) => {
                          if (selectedMapRef.current[j.id]?.addStatus) {
                            return { ...j, isChecked: newValue };
                          }
                          return j;
                        }),
                      }));
                      setExistingUsers(tmp);
                      existingUsersRef.current = tmp;
                      setCompanyGroups((prev) => prev.map((g) => ({
                        ...g,
                        users: g.users.map((cu) => selectedMapRef.current[cu.id]?.addStatus ? { ...cu, isChecked: newValue } : cu),
                      })));
                    }}
                  >
                    <ModernToggleSwitch
                      checked={Object.values(selectedMapRef.current).filter((u) => u.addStatus).length > 0 &&
                        Object.values(selectedMapRef.current).filter((u) => u.addStatus).every((u) => u.isChecked)}
                      onChange={() => {}}
                      id="custom-switch-set-all"
                      variant={props.isInternal ? "green" : "orange"}
                    />
                    <span className="add-project-chat-user__toggle-all-label">
                      {strings.allow_access_chat} ({isGerm ? "für alle" : "for all"})
                    </span>
                  </div>
                )}
                {!props.isInternal && !continueAction && companyGroups.length > 0 && (
                  <div className={`company-accordion-section add-project-chat-user__accordion${search ? " add-project-chat-user__accordion--search" : ""}`}>
                    {companyGroups.map((cg) => {
                      const isOpen = openCompanies.has(cg.companyId);
                      const visibleUsers = _.uniqBy(
                        search
                          ? cg.users.filter((u) => {
                              const lower = search.toLowerCase();
                              const parts = lower.split(" ");
                              if (parts.length > 1) {
                                const first = parts[0], rest = parts.slice(1).join(" ");
                                return (
                                  (u.firstName || '').toLowerCase().startsWith(first) && (u.lastName || '').toLowerCase().startsWith(rest)
                                ) || (
                                  (u.lastName || '').toLowerCase().startsWith(first) && (u.firstName || '').toLowerCase().startsWith(rest)
                                );
                              }
                              return (u.firstName || '').toLowerCase().startsWith(lower) || (u.lastName || '').toLowerCase().startsWith(lower);
                            })
                          : cg.users,
                        "id"
                      );
                      if (search && visibleUsers.length === 0) return null;
                      return (
                        <div key={cg.companyId} className="add-project-chat-user__company">
                          <div
                            onClick={() => {
                              setOpenCompanies((prev) => {
                                const next = new Set(prev);
                                if (next.has(cg.companyId)) next.delete(cg.companyId);
                                else next.add(cg.companyId);
                                return next;
                              });
                            }}
                            className="add-project-chat-user__company-head"
                          >
                            <span className="add-project-chat-user__company-name">
                              {cg.companyName} ({visibleUsers.length})
                            </span>
                            <span className="material-symbols-outlined add-project-chat-user__company-caret">
                              {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                            </span>
                          </div>
                          {isOpen && visibleUsers.length > 0 && (() => {
                            const allSelected = visibleUsers.every((u) => !!selectedMapRef.current[u.id]?.addStatus);
                            return (
                            <div className="add-project-chat-user__company-list">
                              <div
                                onClick={() => {
                                  const newStatus = !allSelected;
                                  visibleUsers.forEach((cu) => {
                                    if (newStatus) {
                                      selectedMapRef.current[cu.id] = { ...cu, addStatus: true, isChecked: !!selectedMapRef.current[cu.id]?.isChecked };
                                    } else {
                                      delete selectedMapRef.current[cu.id];
                                    }
                                  });
                                  setCompanyGroups((prev) => prev.map((g) => ({
                                    ...g,
                                    users: g.users.map((cu) => {
                                      if (visibleUsers.some((vu) => vu.id === cu.id)) return { ...cu, addStatus: newStatus };
                                      return cu;
                                    }),
                                  })));
                                  const userIds = new Set(visibleUsers.map((u) => u.id));
                                  let tmp = [...existingUsers];
                                  tmp = tmp.map((e) => ({
                                    ...e,
                                    names: e.names.map((j) => userIds.has(j.id) ? { ...j, addStatus: newStatus } : j),
                                  }));
                                  setExistingUsers(tmp);
                                  existingUsersRef.current = tmp;
                                }}
                                className="mx-1 add-project-chat-user__select-all"
                              >
                                <div className="row m-0 mx-1 align-items-center">
                                  <div className="col-md-10 p-0">
                                    <span className="add-project-chat-user__select-all-label">
                                      {isGerm ? "Alle auswählen" : "Select all"}
                                    </span>
                                  </div>
                                  <div className="col-md-2">
                                    <div className="custom_checkbox">
                                      <label className={`${props.isInternal ? "role-label-internal" : "role-label"} add-project-chat-user__check-label`}>
                                        <input disabled checked={allSelected} type="checkbox" onClick={() => {}} />
                                        <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="admin-content add-project-chat-user__company-users">
                              {_.sortBy(visibleUsers, [(u) => u?.lastName?.toLowerCase()]).map((u, i) => {
                                const selected = !!selectedMapRef.current[u.id]?.addStatus;
                                const checked = !!selectedMapRef.current[u.id]?.isChecked;
                                return (
                                  <div key={`company-${cg.companyId}-${u.id}`} className="admin-contact" onClick={() => {
                                    const newStatus = !selected;
                                    if (newStatus) {
                                      selectedMapRef.current[u.id] = { ...u, addStatus: true, isChecked: checked };
                                    } else {
                                      delete selectedMapRef.current[u.id];
                                    }
                                    setCompanyGroups((prev) => prev.map((g) => ({
                                      ...g,
                                      users: g.users.map((cu) => cu.id === u.id ? { ...cu, addStatus: newStatus } : cu),
                                    })));
                                    let tmp = [...existingUsers];
                                    tmp = tmp.map((e) => ({
                                      ...e,
                                      names: e.names.map((j) => j.id === u.id ? { ...j, addStatus: newStatus } : j),
                                    }));
                                    setExistingUsers(tmp);
                                    existingUsersRef.current = tmp;
                                  }}>
                                    <div className="row m-0 mx-1">
                                      <div className="col-md-10 p-0">
                                        <div className="admin-control add-project-chat-user__control">
                                          <img
                                            width={30}
                                            height={30}
                                            className="news_profile add-project-chat-user__avatar"
                                            src={u.avatar ? BASE_URL + u.avatar : process.env.PUBLIC_URL + "/assets/img/head.svg"}
                                            alt="Generic placeholder"
                                          />
                                          <h4>
                                            {u.fullName ? convertCapitalLeter(u.fullName) : ""}
                                            <span>{u.companyName}</span>
                                            {u.position ? <span>Position: {convertPosition(isGerm, u.position)}</span> : ""}
                                          </h4>
                                        </div>
                                      </div>
                                      <div className="col-md-2">
                                        <div className="custom_checkbox">
                                          <label className={props.isInternal ? "role-label-internal" : "role-label"}>
                                            <input disabled checked={selected} type="checkbox" value={u.id} onClick={() => {}} />
                                            <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                    {(props.addAsMember || props.groupId) && (
                                      <div onClick={(e) => e.stopPropagation()}>
                                        <div className={`${props.isInternal ? "project-checkbox-internal" : "project-checkbox"} add-project-chat-user__chat-access`}>
                                          <ModernToggleSwitch
                                            checked={checked}
                                            onChange={async () => {
                                              await confirmWithColor({
                                                title: strings.confirm,
                                                textContent: isGerm ? (checked ? DE.toggle_chat_history_off : DE.toggle_chat_history_on) : (checked ? EN.toggle_chat_history_off : EN.toggle_chat_history_on),
                                                options: { ok: strings.confirm, cancel: strings.cancel }
                                              });
                                              const newChecked = !checked;
                                              if (selectedMapRef.current[u.id]) {
                                                selectedMapRef.current[u.id] = { ...selectedMapRef.current[u.id], isChecked: newChecked };
                                              }
                                              setCompanyGroups((prev) => prev.map((g) => ({
                                                ...g,
                                                users: g.users.map((cu) => cu.id === u.id ? { ...cu, isChecked: newChecked } : cu),
                                              })));
                                              let tmp = [...existingUsers];
                                              tmp = tmp.map((e) => ({
                                                ...e,
                                                names: e.names.map((j) => j.id === u.id ? { ...j, isChecked: newChecked } : j),
                                              }));
                                              setExistingUsers(tmp);
                                              existingUsersRef.current = tmp;
                                            }}
                                            id={`custom-switch-company-${u.id}`}
                                            variant={props.isInternal ? "green" : "orange"}
                                          />
                                          <span className={`add-project-chat-user__chat-access-label${checked ? (props.isInternal ? " add-project-chat-user__chat-access-label--on-internal" : " add-project-chat-user__chat-access-label--on") : ""}`}>
                                            {strings.allow_access_chat}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {!search && (
                      <div className="add-project-chat-user__divider" />
                    )}
                  </div>
                )}
                {search && !continueAction && (() => {
                  const companyUserIds = !props.isInternal
                    ? new Set(companyGroups.flatMap((cg) => cg.users.map((cu) => cu.id)))
                    : new Set();
                  return (
                  <div className="admin-content">
                    {existingUsers &&
                      existingUsers.flatMap((user) =>
                        _.sortBy(
                          user.names.filter((n) => !companyUserIds.has(n.id)),
                          [(us) => us?.lastName?.toLowerCase()],
                          ["lastName"]
                        ).map((u, i) => (
                          <div key={`${user.name}-${u.id || i}`} className="admin-contact" onClick={() => {
                            const newStatus = !u.addStatus;
                            if (newStatus) {
                              selectedMapRef.current[u.id] = { ...u, addStatus: true };
                            } else {
                              delete selectedMapRef.current[u.id];
                            }
                            let tmp = [...existingUsers]
                            let ids = []
                            tmp = tmp.map(e => {
                              e.names = e.names.map(j => {
                                return { ...j, addStatus: (u.id == j.id) ? newStatus : j.addStatus }
                              })
                              let x = e.names.filter(xs => xs.addStatus)
                              if (x?.length == e.names.length)
                                ids = [...ids, { ...e }]
                              return { ...e }
                            })
                            if (ids.length == tmp.length) {
                              setIsAllMember(true)
                            }
                            else {
                              setIsAllMember(false)
                            }
                            setExistingUsers([...tmp])
                            existingUsersRef.current = [...tmp];
                            setCompanyGroups((prev) => prev.map((g) => ({
                              ...g,
                              users: g.users.map((cu) => cu.id === u.id ? { ...cu, addStatus: newStatus } : cu),
                            })));
                          }}>
                            <div className="row m-0 mx-1">
                              <div className="col-md-10 p-0">
                                <div className="admin-control add-project-chat-user__control">
                                  <img
                                    width={30}
                                    height={30}
                                    className={`news_profile add-project-chat-user__avatar add-project-chat-user__avatar--square${(u.isKjuup || u.companies?.some(c => c.isKjuup)) ? " add-project-chat-user__avatar--kjuup" : ""}`}
                                    src={
                                      u.avatar
                                        ? BASE_URL + u.avatar
                                        : process.env.PUBLIC_URL +
                                        "/assets/img/head.svg"
                                    }
                                    alt="Generic placeholder"
                                  />
                                  <h4>
                                    {u.fullName
                                      ? convertCapitalLeter(u.fullName)
                                      : ""}
                                    <span>{u.details}</span>
                                    <span>{u.companyName}</span>
                                    {u.position ? (
                                      <span>
                                        Position:{" "}
                                        {convertPosition(
                                          isGerm,
                                          u.position
                                        )}
                                      </span>
                                    ) : (
                                      ""
                                    )}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-md-2">
                                <div className="custom_checkbox">
                                  <label className={props.isInternal ? "role-label-internal" : "role-label"}>
                                    <input
                                      disabled
                                      checked={u.addStatus}
                                      type="checkbox"
                                      value={u.id}
                                      onClick={(e) => { }}
                                    />
                                    <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                                  </label>
                                </div>
                              </div>
                            </div>
                            {(props.addAsMember || props.groupId) && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <div className={`${props.isInternal ? "project-checkbox-internal" : "project-checkbox"} add-project-chat-user__chat-access`}>
                                  <ModernToggleSwitch
                                    checked={u.isChecked ? u.isChecked : false}
                                    onChange={async () => {
                                      let tmp = [...existingUsers]
                                      await confirmWithColor({
                                        title: strings.confirm,
                                        textContent: isGerm ? u.isChecked ? DE.toggle_chat_history_off : DE.toggle_chat_history_on : u.isChecked ? EN.toggle_chat_history_off : EN.toggle_chat_history_on,
                                        options: {
                                          ok: strings.confirm,
                                          cancel: strings.cancel
                                        }
                                      });
                                      setCheckToggle({ index: user.names.indexOf(u), id: u.id })
                                    }}
                                    id={`custom-switch-${u.id}`}
                                    variant={props.isInternal ? "green" : "orange"}
                                  />
                                  <span className={`add-project-chat-user__chat-access-label${u.isChecked ? (props.isInternal ? " add-project-chat-user__chat-access-label--on-internal" : " add-project-chat-user__chat-access-label--on") : ""}`}>
                                    {strings.allow_access_chat}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                  </div>
                  );
                })()}
                {
                  (existingUsers?.length > 0 && !continueAction && !search && props.isInternal) ?
                    <div
                      onClick={() => {
                        let inn = !isAllMember
                        setIsAllMember(inn)
                        let tmp = [...existingUsers]
                        tmp = tmp.map(e => {
                          e.names = e.names.map(j => {
                            const updated = { ...j, addStatus: inn };
                            if (inn) {
                              selectedMapRef.current[j.id] = { ...j, addStatus: true };
                            } else {
                              delete selectedMapRef.current[j.id];
                            }
                            return updated;
                          })
                          return { ...e }
                        })
                        setExistingUsers([...tmp])
                        existingUsersRef.current = [...tmp];
                      }}
                      className="mx-1 my-3 kj-pointer"
                    >
                      <div className="row m-0 mx-1 align-items-center">
                        <div className="col-md-10 p-0">
                          <span className="add-project-chat-user__invite-all-label">
                            {strings.invite_all_members}
                          </span>
                        </div>
                        <div className="col-md-2">
                          <div className="custom_checkbox">
                            <label className={`${props.isInternal ? "role-label-internal" : "role-label"} add-project-chat-user__check-label`}>
                              <input
                                disabled
                                checked={isAllMember}
                                type="checkbox"
                                className="add-project-chat-user__invite-all-input"
                              />
                              <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    :
                    <></>
                }
                {!search && !continueAction && (() => {
                  const companyUserIds = !props.isInternal
                    ? new Set(companyGroups.flatMap((cg) => cg.users.map((cu) => cu.id)))
                    : new Set();
                  return (
                  <div className="admin-content">
                    {existingUsers &&
                      existingUsers.flatMap((user) =>
                        _.sortBy(
                          user.names.filter((n) => !companyUserIds.has(n.id)),
                          [(us) => us?.lastName?.toLowerCase()],
                          ["lastName"]
                        ).map((u, i) => (
                          <div key={`${user.name}-${i}`} className="admin-contact" onClick={() => {
                            const newStatus = !u.addStatus;
                            selectedMapRef.current[u.id] = { ...u, addStatus: newStatus };
                            if (!newStatus) delete selectedMapRef.current[u.id];
                            let tmp = [...existingUsers]
                            let ids = []
                            tmp = tmp.map(e => {
                              e.names = e.names.map(j => {
                                return { ...j, addStatus: (u.id == j.id) ? newStatus : j.addStatus }
                              })
                              let x = e.names.filter(xs => xs.addStatus)
                              if (x?.length == e.names.length)
                                ids = [...ids, { ...e }]
                              return { ...e }
                            })
                            if (ids.length == tmp.length) {
                              setIsAllMember(true)
                            }
                            else {
                              setIsAllMember(false)
                            }
                            setExistingUsers([...tmp])
                            existingUsersRef.current = [...tmp];
                            setCompanyGroups((prev) => prev.map((g) => ({
                              ...g,
                              users: g.users.map((cu) => cu.id === u.id ? { ...cu, addStatus: newStatus } : cu),
                            })));
                          }}>
                            <div className="row m-0 mx-1">
                              <div className="col-md-10 p-0">
                                <div className="admin-control add-project-chat-user__control">
                                  <img
                                    width={30}
                                    height={30}
                                    className={`news_profile add-project-chat-user__avatar${(u.isKjuup || u.companies?.some(c => c.isKjuup)) ? " add-project-chat-user__avatar--kjuup" : ""}`}
                                    src={
                                      u.avatar
                                        ? BASE_URL + u.avatar
                                        : process.env.PUBLIC_URL +
                                        "/assets/img/head.svg"
                                    }
                                    alt="Generic placeholder"
                                  />
                                  <h4>
                                    {u.fullName
                                      ? convertCapitalLeter(u.fullName)
                                      : ""}
                                    <span>{u.details}</span>
                                    <span>{u.companyName}</span>
                                    {u.position ? (
                                      <span>
                                        Position:{" "}
                                        {convertPosition(
                                          isGerm,
                                          u.position
                                        )}
                                      </span>
                                    ) : (
                                      ""
                                    )}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-md-2">
                                <div className="custom_checkbox">
                                  <label className={props.isInternal ? "role-label-internal" : "role-label"}>
                                    <input
                                      disabled
                                      checked={u.addStatus}
                                      type="checkbox"
                                      value={u.id}
                                      onClick={(e) => { }}
                                    />
                                    <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                                  </label>
                                </div>
                              </div>
                            </div>
                            {(props.addAsMember || props.groupId) && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <div className={`${props.isInternal ? "project-checkbox-internal" : "project-checkbox"} add-project-chat-user__chat-access`}>
                                  <ModernToggleSwitch
                                    checked={u.isChecked ? u.isChecked : false}
                                    onChange={async () => {
                                      let tmp = [...existingUsers]
                                      await confirmWithColor({
                                        title: strings.confirm,
                                        textContent: isGerm ? u.isChecked ? DE.toggle_chat_history_off : DE.toggle_chat_history_on : u.isChecked ? EN.toggle_chat_history_off : EN.toggle_chat_history_on,
                                        options: {
                                          ok: strings.confirm,
                                          cancel: strings.cancel
                                        }
                                      });
                                      setCheckToggle({ index: user.names.indexOf(u), id: u.id })
                                    }}
                                    id={`custom-switch-${u.id}`}
                                    variant={props.isInternal ? "green" : "orange"}
                                  />
                                  <span className={`add-project-chat-user__chat-access-label${u.isChecked ? (props.isInternal ? " add-project-chat-user__chat-access-label--on-internal" : " add-project-chat-user__chat-access-label--on") : ""}`}>
                                    {strings.allow_access_chat}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                  </div>
                  );
                })()}
                {loadingMore && (
                  <div className="add-project-chat-user__loading-more">
                    <div className={`loader add-project-chat-user__loader${props.isInternal ? " add-project-chat-user__loader--internal" : ""}`} />
                  </div>
                )}
                {continueAction && existingUsers && (
                  <div className="admin-content">
                    {existingUsers &&
                      existingUsers.flatMap((user) =>
                        _.sortBy(
                          user.names.filter(u => u.addStatus),
                          [(us) => us?.lastName?.toLowerCase()],
                          ["lastName"]
                        ).map((u, i) => (
                          <div key={`${user.name}-${i}`} className="admin-contact">
                            <div className="row m-0 mx-1">
                              <div className="col-md-10 p-0">
                                <div className="admin-control add-project-chat-user__control">
                                  <img
                                    width={30}
                                    height={30}
                                    className={`news_profile add-project-chat-user__avatar${(u.isKjuup || u.companies?.some(c => c.isKjuup)) ? " add-project-chat-user__avatar--kjuup" : ""}`}
                                    src={
                                      u.avatar
                                        ? BASE_URL + u.avatar
                                        : process.env.PUBLIC_URL +
                                        "/assets/img/head.svg"
                                    }
                                    alt="Generic placeholder"
                                  />
                                  <h4>
                                    {u.fullName
                                      ? convertCapitalLeter(u.fullName)
                                      : ""}
                                    <span>{u.details}</span>
                                    <span>{u.companyName}</span>
                                    {u.position ? (
                                      <span>
                                        Position:{" "}
                                        {convertPosition(
                                          isGerm,
                                          u.position
                                        )}
                                      </span>
                                    ) : (
                                      ""
                                    )}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-md-2">
                                <div className="custom_checkbox">
                                  <label className={props.isInternal ? "role-label-internal" : "role-label"}>
                                    <input
                                      disabled
                                      checked={u.addStatus}
                                      type="checkbox"
                                      value={u.id}
                                      onClick={(e) => { }}
                                    />
                                    <span className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"} />
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                  </div>
                )}
                {continueAction && pickedTeams.length > 0 && (
                  <div className="admin-content add-project-chat-user__teams-picked">
                    {pickedTeams.map((p) => (
                      <div key={`teams-${p.aadUserId}`} className="kjc-teams-new__person is-selected">
                        <ChatListAvatar kind="direct" name={teamsPersonName(p)} teams />
                        <span className="kjc-teams-new__person-text">
                          <span className="kjc-teams-new__person-name">{teamsPersonName(p)}</span>
                          {p.email || p.jobTitle ? (
                            <span className="kjc-teams-new__person-sub">{[p.jobTitle, p.email].filter(Boolean).join(" · ")}</span>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="add-project-chat-user__spacer" />
            </div>
          </CustomScrollbars>
          </div>
        </div>
      </div>

      {/* Creation Transition Screen */}
      {creationStep && (
        <div
          className="creation-transition-overlay add-project-chat-user__creation"
        >
          <div className="add-project-chat-user__creation-stack">
            <div className={`creation-spinner add-project-chat-user__creation-spinner${props.isInternal ? " add-project-chat-user__creation-spinner--internal" : ""}`} />
            <div className="add-project-chat-user__creation-steps">
              <div className={`add-project-chat-user__creation-step${creationStep === 'adding_members' ? " add-project-chat-user__creation-step--active" : (creationStep === 'creating_chat' || creationStep === 'finishing' ? " add-project-chat-user__creation-step--done" : "")}`}>
                <span className={`material-symbols-outlined add-project-chat-user__creation-icon${(creationStep === 'creating_chat' || creationStep === 'finishing') ? (props.isInternal ? " add-project-chat-user__creation-icon--done-internal" : " add-project-chat-user__creation-icon--done") : ""}`}>
                  {(creationStep === 'creating_chat' || creationStep === 'finishing') ? 'check_circle' : 'group_add'}
                </span>
                <span className={`add-project-chat-user__creation-label${creationStep === 'adding_members' ? " add-project-chat-user__creation-label--active" : ""}`}>
                  {strings.adding_members || "Adding Members..."}
                </span>
              </div>
              <div className={`add-project-chat-user__creation-step${creationStep === 'creating_chat' ? " add-project-chat-user__creation-step--active" : (creationStep === 'finishing' ? " add-project-chat-user__creation-step--done" : "")}`}>
                <span className={`material-symbols-outlined add-project-chat-user__creation-icon${creationStep === 'finishing' ? (props.isInternal ? " add-project-chat-user__creation-icon--done-internal" : " add-project-chat-user__creation-icon--done") : ""}`}>
                  {creationStep === 'finishing' ? 'check_circle' : 'chat_bubble'}
                </span>
                <span className={`add-project-chat-user__creation-label${creationStep === 'creating_chat' ? " add-project-chat-user__creation-label--active" : ""}`}>
                  {strings.creating_the_chat || "Creating the Chat..."}
                </span>
              </div>
              <div className={`add-project-chat-user__creation-step${creationStep === 'finishing' ? " add-project-chat-user__creation-step--active" : ""}`}>
                <span className="material-symbols-outlined add-project-chat-user__creation-icon">
                  sync
                </span>
                <span className={`add-project-chat-user__creation-label${creationStep === 'finishing' ? " add-project-chat-user__creation-label--active" : ""}`}>
                  {strings.finishing_setup || "Finishing Setup..."}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Confirmation Modal */}
      {showPrivacyModal && (
        <div
          className="create-message-modal-overlay add-project-chat-user__privacy"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="add-project-chat-user__privacy-card"
          >
            <h4 className="add-project-chat-user__privacy-title">
              {strings.professional_or_private}
            </h4>
            <p className="add-project-chat-user__privacy-text">
              {strings.privacy_modal_text_updated}
            </p>
            <div className="add-project-chat-user__privacy-actions">
              <button
                onClick={() => handlePrivacyChoice(true)}
                disabled={loader}
                className={`kj-btn kj-btn--outline kj-btn--on-surface kj-flex-1${props.isInternal ? ' kj-btn--internal' : ''}`}
              >
                {strings.private}
              </button>
              <button
                onClick={() => handlePrivacyChoice(false)}
                disabled={loader}
                className={`kj-btn kj-btn--primary kj-flex-1${props.isInternal ? ' kj-btn--internal' : ''}`}
              >
                {strings.professional}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default AddProjectChatUser;
